import React, { useEffect, useMemo, useState } from 'react'
import { submitResourceListingRequest as submitResourceListingInquiry } from '@/lib/api/public'
import { listPublicResources, type ResourceDirectoryRecord } from '@/lib/api/stabilization'
import { Badge, Button, Card, Input, Spinner } from '@/components/ui'

export default function ResourceDirectoryContent() {
  const [resources, setResources] = useState<ResourceDirectoryRecord[]>([])
  const [showAgencyForm, setShowAgencyForm] = useState(false)
  const [agencyForm, setAgencyForm] = useState({ organization_name: '', contact_name: '', email: '', phone: '', website: '', service_category: '', service_area: '', description: '', notes: '' })
  const [agencySubmitting, setAgencySubmitting] = useState(false)
  const [agencyMessage, setAgencyMessage] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    listPublicResources()
      .then(setResources)
      .catch((err) => setError(err instanceof Error ? err.message : 'Unable to load resources.'))
      .finally(() => setLoading(false))
  }, [])

  const categories = useMemo(
    () => [...new Set(resources.map((r) => r.category))].sort(),
    [resources]
  )

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return resources.filter((r) => {
      const matchesCategory = !category || r.category === category
      const haystack = [r.organization_name, r.service_description, r.category, r.service_area]
        .filter(Boolean).join(' ').toLowerCase()
      return matchesCategory && (!term || haystack.includes(term))
    })
  }, [resources, search, category])

  return (
    <div className="space-y-8">
      <div className="grid gap-4 md:grid-cols-[1fr_220px]">
        <Input
          label="Search resources"
          placeholder="Housing, food, transportation, healthcare..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="flex flex-col gap-1 text-sm font-medium text-gray-700 dark:text-gray-300">
          Category
          <select
            className="h-10 rounded-lg border border-gray-300 bg-white px-3 dark:bg-gray-900 dark:border-gray-700"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">All categories</option>
            {categories.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </label>
      </div>

      {loading && <div className="flex justify-center py-16"><Spinner size="lg" /></div>}
      {error && (
        <Card className="border-red-200">
          <p className="font-semibold text-red-700">Resource directory unavailable</p>
          <p className="mt-1 text-sm text-gray-600">{error}</p>
        </Card>
      )}

      {!loading && !error && (
        <>
          <div className="rounded-2xl border border-brand-100 bg-brand-50 p-4">
            <p className="font-semibold text-gray-900">🌱 W.E.E. Community Resource Network</p>
            <p className="mt-1 text-sm text-gray-600">
              {resources.length} verified public resource{resources.length === 1 ? '' : 's'} are currently listed. This directory will continue to grow as community agencies and organizations submit their information to W.E.E. for review and inclusion.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            {filtered.map((resource) => (
              <Card key={resource.id} hover>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{resource.organization_name}</h2>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{resource.service_description}</p>
                  </div>
                  <Badge color={resource.status === 'Active' ? 'green' : 'yellow'}>{resource.status}</Badge>
                </div>
                <dl className="mt-5 space-y-2 text-sm">
                  {resource.category && <div><dt className="font-medium inline">Category: </dt><dd className="inline">{resource.category}</dd></div>}
                  {resource.service_area && <div><dt className="font-medium inline">Service area: </dt><dd className="inline">{resource.service_area}</dd></div>}
                  {resource.phone && <div><dt className="font-medium inline">Phone: </dt><dd className="inline">{resource.phone}</dd></div>}
                  {resource.website && (
                    <div>
                      <dt className="font-medium inline">Website: </dt>
                      <dd className="inline"><a className="text-brand-600 hover:underline" href={resource.website} target="_blank" rel="noreferrer">Visit resource</a></dd>
                    </div>
                  )}
                </dl>
                {resource.last_verified && <p className="mt-4 text-xs text-gray-500">Last verified: {resource.last_verified}</p>}
              </Card>
            ))}
          </div>

          {filtered.length === 0 && (
            <Card><p className="text-gray-600 dark:text-gray-300">No matching resources found.</p></Card>
          )}

          <div className="rounded-3xl border-2 border-brand-200 bg-white p-6 md:p-7 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-brand-600">Community agency opportunity</p>
                <h2 className="text-xl font-bold text-gray-900 mt-1">Want your organization included?</h2>
                <p className="mt-1 text-sm text-gray-600">Submit your organization or resource information here. W.E.E. reviews submissions before anything is added to the public directory.</p>
              </div>
              <Button variant="primary" onClick={() => { setShowAgencyForm(v => !v); setAgencyMessage(null) }}>
                {showAgencyForm ? 'Close Listing Request' : 'Submit Organization Information →'}
              </Button>
            </div>

            {showAgencyForm && (
              <div className="mt-5 rounded-2xl border border-brand-200 bg-brand-50/40 p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-bold text-gray-900">Community Resource Listing Request</h3>
                    <p className="mt-1 text-sm text-gray-600">This is a review request, not an automatic directory listing. W.E.E. will verify the information before publication.</p>
                  </div>
                  <button type="button" onClick={() => setShowAgencyForm(false)} className="text-gray-400 hover:text-gray-700 text-xl" aria-label="Close organization submission form">×</button>
                </div>
                <form className="mt-5 grid gap-4 md:grid-cols-2" onSubmit={async (e) => {
                  e.preventDefault()
                  setAgencySubmitting(true)
                  setAgencyMessage(null)
                  try {
                    await submitResourceListingInquiry(agencyForm)
                    setAgencyMessage('Thank you! Your organization information has been submitted to W.E.E. for review.')
                    setAgencyForm({ organization_name: '', contact_name: '', email: '', phone: '', website: '', service_category: '', service_area: '', description: '', notes: '' })
                  } catch (err) {
                    setAgencyMessage(err instanceof Error ? err.message : 'Unable to submit right now. Please try again.')
                  } finally {
                    setAgencySubmitting(false)
                  }
                }}>
                  {['organization_name','contact_name','email','phone','website','service_category','service_area'].map((field) => (
                    <label key={field} className="text-sm font-medium text-gray-700">
                      {field === 'organization_name' ? 'Organization Name' : field === 'contact_name' ? 'Primary Contact Name' : field === 'service_category' ? 'Primary Service Category' : field === 'service_area' ? 'Service Area' : field.charAt(0).toUpperCase() + field.slice(1)}
                      <input
                        required={['organization_name','contact_name','email'].includes(field)}
                        type={field === 'email' ? 'email' : 'text'}
                        value={agencyForm[field as keyof typeof agencyForm]}
                        onChange={(e) => setAgencyForm((prev) => ({ ...prev, [field]: e.target.value }))}
                        className="mt-1 w-full rounded-xl border border-gray-300 px-4 py-3 font-normal text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-500"
                      />
                    </label>
                  ))}
                  <label className="text-sm font-medium text-gray-700 md:col-span-2">What does your organization provide?<textarea required rows={3} value={agencyForm.description} onChange={(e) => setAgencyForm((prev) => ({ ...prev, description: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-300 px-4 py-3 font-normal text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-500" /></label>
                  <label className="text-sm font-medium text-gray-700 md:col-span-2">Additional notes<textarea rows={2} value={agencyForm.notes} onChange={(e) => setAgencyForm((prev) => ({ ...prev, notes: e.target.value }))} className="mt-1 w-full rounded-xl border border-gray-300 px-4 py-3 font-normal text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-500" /></label>
                  {agencyMessage && <p className="md:col-span-2 rounded-xl bg-white border border-brand-100 p-3 text-sm text-gray-700">{agencyMessage}</p>}
                  <div className="md:col-span-2"><Button type="submit" variant="primary" disabled={agencySubmitting}>{agencySubmitting ? 'Submitting…' : 'Submit for W.E.E. Review'}</Button></div>
                </form>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
